import React, { useRef } from "react";
import LogOutUser from "components/account/log-out";
import { DirectLink } from "components/link-types";
import { useSelector, shallowEqual } from "react-redux";
import Link from "helpers/link";
import { onKeyDown } from "helpers/dom-events";
import { ReactComponent as User } from "assets/svgs/user.svg";
import { IconLink } from "components/link-types";
import { useGlobalContext } from "context/global";
import Image from "components/image";
import { subscriptionPageUrl } from "utils/constants";

export const UserStatus = ({ customerLinks, path }) => {
	const { isAuthenticated, firstName, displayName } = useSelector(
		(state) => ({
			isAuthenticated: state.account.profile?.isAuthenticated,
			firstName: state.account.profile?.firstName,
			displayName: state.account.profile?.displayName,
		}),
		shallowEqual
	);
	const { translation, } = useGlobalContext();

	if (isAuthenticated) {
		return (
			<div className="group text-center whitespace-nowrap relative mr-2">
				<Link to={subscriptionPageUrl}
					className="group-hover:border-2 border-brown pt-1 pr-6 pl-5 rounded-3xl border flex items-center">
					<span className="[transform:translateZ(0)]">
						<User className="scale-[0.78] user-icon mb-1 mr-1" role="presentation" aria-hidden="true" />
					</span>
					<span className="block text-lg">{firstName || displayName} </span>
				</Link>

				<div className="customer-links">
					{customerLinks.map((link) =>
						link._type === "iconLink" ?
							<IconLink key={link?._key} link={link} path={path} className="!py-0 mt-3 px-4 text-lg" />
							: <LogOutUser icon key={link?._key} text={link?.title} textColor={link.color?.hex}
								className="mt-3 px-4 block text-lg hover:underline" />)}
				</div>
			</div>
		);
	}

	return (
		<nav aria-label="User menu">
			<Link
				to="/account/login"
				aria-label="Login"
				className="focus-outline-ay11 border-brown py-0 pt-1 pr-6 pl-5 rounded-3xl border flex items-center"
			>
				<User className="scale-[0.78] user-icon md:mb-1 md:mr-1" role="presentation" aria-hidden="true" />
				<span className="hidden md:block">{translation.other?.login}</span>
			</Link>
		</nav>
	);
};

const GroupLinks = ({ data, advertisement, isAuthenticated }) => {
	const groupedLinksRef = useRef(null);

	const shouldShowAdvertisement = (advertisement && !isAuthenticated);
	const handleOnBlur = () => groupedLinksRef.current.classList.remove("show-dropdown");
	const groupLinkDataLength = data.groupLinkData.length;

	return (
		<div className="group text-center whitespace-nowrap">
			<Link to={data?.url}>
				{data?.url ? (
					<p
						role="text"
						tabIndex="-1"
						aria-label={data.title}
						title={data.title}
						className="nav-link group-hover:font-boldParagraph"
					>
						{data.title}
					</p>
				) : (
					<p
						role="button"
						tabIndex="0"
						onKeyDown={onKeyDown(() => groupedLinksRef.current.classList.add("show-dropdown"))}
						aria-label={data.title}
						title={data.title}
						className="nav-link group-hover:font-boldParagraph"
					>
						{data.title}
					</p>
				)}
			</Link>

			<div
				ref={groupedLinksRef}
				className="group-links-container"

			>
				<div className="ml-[7.5rem] p-4 flex justify-between">
					<div>
						{data.groupLinkData.map((link, index) =>
							link._type === "groupLinkData" ? (
								<Link
									to={link?.url}
									className="w-fit mt-3 block menu-item focus:border-[#D0331F]"
									key={link?._key}
									onBlur={
										/*
										 *	! Keyboard Accessibility !
										 *
										 *	If the advertisement is not shown then the last link hides the dropdown
										 *	when it loses focus!
										 *
										 *	Else if the advertisement is shown then it will close the dropdown
										 *	on focus out.
										 * */
										!shouldShowAdvertisement
											? ((index + 1) === groupLinkDataLength ? handleOnBlur : () => { })
											: () => { }
									}
								>
									{link.title}
								</Link>
							) : link._type === "logOutLink" ? (
								<LogOutUser
									key={link._key}
									textColor={link.color?.hex}
									className="w-fit py-8 px-4 group-hover:flex block focus:border-[#D0331F]"
								/>
							) : null
						)}
					</div>
					{shouldShowAdvertisement && (
						<div className="group top-0 flex flex-col w-60 gap-2 mr-16 mt-4">
							<Image
								image={advertisement?.image}
								to={advertisement?.image?.url}
								className="w-60"
							/>
							<Link
								onBlur={handleOnBlur}
								to={advertisement?.image?.url}
								className="no-underline text-lg font-boldParagraph text-left hover:underline focus:border-[#D0331F]"
							>
								{advertisement?.title}
							</Link>
							<span className="text-sm text-left text-wrap whitespace-normal">
								{advertisement?.subtitle}
							</span>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

const DesktopHeaderData = ({ data, advertisement }) => {
	if (!data?.length) return <></>;
	const isAuthenticated = useSelector((state) => state.account.profile.isAuthenticated);

	return (
		<div className="flex justify-between items-center relative">
			{
				data.map((link) => {
					switch (link._type) {
						case "groupLink":
							return (
								<GroupLinks
									isAuthenticated={isAuthenticated}
									data={link}
									key={link?._key}
									advertisement={advertisement}
								/>
							);
						case "directLink":
							return (
								<DirectLink
									data={link}
									key={link._key}
									style="nav-link border-b-0 text-lg !py-0"
								/>
							);
						case "logOutLink":
							return (
								<LogOutUser
									key={link._key}
									textColor={link?.color?.hex}
									className="nav-link"
								/>
							);
						default:
							return null;
					}
				})
			}
		</div>
	);
};

export default DesktopHeaderData;